'use client'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { motion, useMotionValue, useTransform } from 'motion/react'
import styled from 'styled-components'
import { useDeviceType } from '@/hooks'
import { mq, vw, designGrid } from '@/styles'
import { Link } from '@/components/base'
import { E_HOMEPAGE_LINK_ITEM_TYPE, type TypeCmsHomepageLink } from '@/schema'

interface IRoundHighlightSection {
  data: TypeCmsHomepageLink[]
}

const diameter = 500
const fontSize = 32
const circumference = Math.PI * diameter

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CircleItem = ({ title, image, href }: { title: string, image: any, href: string }) => {
  const string = `\u00A0${title}\u00A0`
  const repeat = Math.ceil((circumference / fontSize) / string.length)
  // const letterSpacing = Math.floor(circumference / (string.length * repeat))
  const rotation = useMotionValue(0)
  const rotate = useTransform(rotation, v => `${v}deg`)
  const rafId = useRef<number>(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    const loop = () => {
      rotation.set(rotation.get() + 0.2)
      rafId.current = requestAnimationFrame(loop)
    }

    if (!paused) {
      rafId.current = requestAnimationFrame(loop)
    }

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current)
    }
  }, [paused, rotation])

  return (
    <motion.li className='circle' onHoverStart={() => setPaused(true)} onHoverEnd={() => setPaused(false)} whileHover={{ scale: 1.1 }} transition={{ duration: 0.5 }}>
      <Link href={href} aria-label={title}>
        <motion.svg style={{ rotate }} viewBox='-179 -179 358 358'>
          <path id='circlePath' fill='none' d='M -144, 0 a 144,144 0 1,1 288,0 a 144,144 0 1,1 -288,0' />
          <text fontFamily='Basier Circle, sans-serif' fontSize='32' fontWeight='bold' fill='#5F895E'>
            <textPath href='#circlePath' spacing='auto'>
              {Array(repeat).fill(title).join(' \u00A0')}
            </textPath>
          </text>
        </motion.svg>
        <div className='image-cropper'>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={image?.default?.src} alt='img' {...image?.default?.size && { ...image?.default?.size }} draggable='false' />
        </div>
      </Link>
    </motion.li>
  )
}

const sliceEndMobile = 1
const sliceEndDesktop = 3

const getHrefByItemType = (item: TypeCmsHomepageLink) => {
  if(item.itemType === E_HOMEPAGE_LINK_ITEM_TYPE.CATEGORY) return '/catalog/' + item.target
  else if(item.itemType === E_HOMEPAGE_LINK_ITEM_TYPE.CUSTOM) return '/' + item.target
  else if(item.itemType === E_HOMEPAGE_LINK_ITEM_TYPE.PRODUCT) return '/catalog/product/' + item.target
  else return '/' + item.itemType + '/' + item.target
}
export const RoundHighlightedCategories = memo<IRoundHighlightSection>(({ data }) => {
  const { isMobile } = useDeviceType()
  const sliceEnd = isMobile ? sliceEndMobile : sliceEndDesktop
  const circles = useMemo(() => data.slice(0, sliceEnd), [data, sliceEnd])

  return (
    <Section>
      <ul>
        {circles.map((item, idx) => (
          <CircleItem key={`${idx}-${item?.title?.length}`} title={item.title} image={item.medium} href={getHrefByItemType(item)} />
        ))}
      </ul>
    </Section>
  )
})


const Section = styled.section`
  ${designGrid({})}
  margin: ${vw(40, 'mobile')} 0;

  ${mq.greaterThan('tablet')} {
    margin: ${vw(40, 'desktop')} 0;
  }

  ul {
    display: flex;
    flex-direction: row;
    grid-column: 1 / span 6;
    gap: 10px;
    justify-content: flex-end;

    ${mq.greaterThan('tablet')} {
      gap: 20px;
      grid-column: 1 / span 12;
    }
  }

  li {
    list-style: none;
  }

  .circle {
    position: relative;
    width: ${vw(360, 'mobile')};
    height: ${vw(360, 'mobile')};

    ${mq.greaterThan('tablet')} {
      width: ${vw(360, 'desktop')};
      height: ${vw(360, 'desktop')};
    }
  }

  a {
    display: grid;
    place-items: center;
    position: relative;
  }

  .image-cropper {
    position: absolute;
    width: ${vw(273, 'mobile')};
    height: ${vw(273, 'mobile')};
    border-radius: 50%;
    overflow: hidden;
    z-index: 1;

    ${mq.greaterThan('tablet')} {
      width: ${vw(273, 'desktop')};
      height: ${vw(273, 'desktop')};
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  svg {
    width: 100%;
    height: 100%;
    z-index: 0;
  }
`
